'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/forms/button';
import { SectionPanel } from '@/components/ui/design-system';

/** Canonical prose treatment for a job description — shared so every surface
 *  (job-details page, Simple Search cards) renders the role identically. */
const PROSE_CLASS =
  'prose prose-sm prose-gray dark:prose-invert max-w-none text-foreground';

interface JobDescriptionPanelProps {
  /** Raw description HTML from the source posting (rendered richly; entities decode natively). */
  html: string;
  /**
   * `page`    — expandable block for the full job-details page (height clamp + "Show full" toggle).
   * `preview` — fills its flex parent and clamps with a bottom fade, no toggle. For
   *             space-constrained surfaces (Simple Search cards) where the full role is one click away.
   */
  variant?: 'page' | 'preview';
  /** `page` only: start expanded (e.g. the job preview dialog reads the role in full). */
  defaultExpanded?: boolean;
  /**
   * `preview` only: when provided, renders a "Show full description" button
   * integrated at the bottom of the clamped block (instead of the caller adding
   * its own). Use it to open the full posting (e.g. the View Job dialog).
   */
  onShowFull?: () => void;
  className?: string;
}

/**
 * The "Job Description" section as it appears on the job-details page, extracted
 * so other surfaces can reuse the exact same framing + prose rendering instead
 * of a stripped-to-text snippet.
 */
export function JobDescriptionPanel({
  html,
  variant = 'page',
  defaultExpanded = false,
  onShowFull,
  className,
}: JobDescriptionPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (variant === 'preview') {
    return (
      <SectionPanel
        title="Job Description"
        bleedOnMobile={false}
        className={cn('flex min-h-0 flex-1 flex-col', className)}
        cardClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
        contentClassName="flex min-h-0 flex-1 flex-col p-0"
      >
        <div className="relative min-h-0 flex-1 overflow-hidden p-3 sm:p-4">
          <div className={PROSE_CLASS} dangerouslySetInnerHTML={{ __html: html }} />
          {/* Fade the clipped tail so it reads as "there's more". */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent"
          />
          {/* "Show full" floats over the fade. A soft card-colored highlight sits
              behind it — solid under the label, fading out to transparent on the
              sides (and blending into the bottom fade) — so the link stays legible
              over the clipped text without a hard footer bar. */}
          {onShowFull ? (
            <div className="absolute inset-x-0 bottom-0 flex justify-center pb-1.5">
              <span
                className="px-6 py-1"
                style={{
                  background:
                    'radial-gradient(56% 130% at 50% 52%, var(--card) 38%, color-mix(in srgb, var(--card) 55%, transparent) 60%, transparent 76%)',
                }}
              >
                <Button
                  variant="link"
                  size="sm"
                  onClick={onShowFull}
                  className="relative h-auto p-0 text-xs font-semibold underline underline-offset-4"
                >
                  Show full description
                </Button>
              </span>
            </div>
          ) : null}
        </div>
      </SectionPanel>
    );
  }

  return (
    <SectionPanel
      title="Job Description"
      bleedOnMobile={false}
      contentClassName="p-4 sm:p-6"
      className={className}
    >
      <div
        className={cn(
          'relative',
          !expanded && 'max-h-[100px] overflow-hidden sm:max-h-[200px]',
        )}
      >
        <div className={PROSE_CLASS} dangerouslySetInnerHTML={{ __html: html }} />
        {!expanded && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-card to-transparent"
          />
        )}
      </div>
      <div className="flex justify-center pt-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExpanded((value) => !value)}
          className="text-xs"
        >
          {expanded ? 'Show less' : 'Show full description'}
        </Button>
      </div>
    </SectionPanel>
  );
}
